import { CapaDeModal } from '@/shared/components/CapaDeModal'
import React, { useEffect, useState } from 'react';
import { X, MessageSquare, Loader2, Building2 } from 'lucide-react';
import { purchaseOrdersRHService } from '@/features/rh/api/purchaseOrdersRH.service';
import type { PurchaseOrderRHComment } from '@/types/purchaseOrderRH.types';
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';

interface ViewCommentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string;
  orderNumber: string;
  onFetchComments?: (orderId: string) => Promise<PurchaseOrderRHComment[]>;
}

export const ViewCommentsModal: React.FC<ViewCommentsModalProps> = ({
  isOpen,
  onClose,
  orderId,
  orderNumber,
  onFetchComments
}) => {
  const [comments, setComments] = useState<PurchaseOrderRHComment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadComments();
    }
  }, [isOpen, orderId]);

  const loadComments = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = onFetchComments
        ? await onFetchComments(orderId)
        : await purchaseOrdersRHService.getComments(orderId);
      setComments(data);
    } catch (err) {
      setError('Error al cargar los comentarios');
      console.error('Error loading comments:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <CapaDeModal>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6">
          <div className="flex items-center gap-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Comentarios
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Orden {orderNumber}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              <span className="ml-3 text-gray-600 dark:text-gray-400">Cargando comentarios...</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600 dark:text-red-400">{error}</p>
              <button
                onClick={loadComments}
                className="mt-4 text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                Reintentar
              </button>
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400">
                No hay comentarios para esta orden
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {comments.map((comment) => (
                <div
                  key={comment.id}
                  className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-4"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                          Interno
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(comment.created_at).toLocaleDateString('es-MX', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                        {comment.comment}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-6">
          <button
            onClick={onClose}
            className={estiloDeBoton('secundario')}
          >
            Cerrar
          </button>
        </div>
      </div>
    </CapaDeModal>
  );
};
